import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requisitionApi, rfqApi, supplierApi } from "../../api";
import { ProcError, ProcForm, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { useAsyncData, useMutation } from "../../hooks";
import type { RFQLine, RfqSupplierInput } from "../../types";

type LineRow = Omit<RFQLine, "id">;

/** One row in the "invited suppliers" picker — either a registered supplier picked from the
 *  dropdown, or a manually-added ad-hoc one (name + email, not yet in the supplier register). */
type SupplierRow =
  | { kind: "registered"; supplierId: number | "" }
  | { kind: "adhoc"; adHocName: string; adHocEmail: string };

export default function RFQCreatePage() {
  const navigate = useNavigate();

  const { data: requisitions, loading: reqsLoading } = useAsyncData(
    () => requisitionApi.list({ size: 100 }).then((r) => r.content),
    [],
  );
  const { data: supplierOptions, loading: suppliersLoading } = useAsyncData(
    () => supplierApi.options(),
    [],
  );

  const [requisitionId, setRequisitionId] = useState<number | "">("");
  const [reqLinesLoading, setReqLinesLoading] = useState(false);
  const [deadline, setDeadline] = useState("");
  const [submissionMethod, setSubmissionMethod] = useState("");
  const [supplierRows, setSupplierRows] = useState<SupplierRow[]>([{ kind: "registered", supplierId: "" }]);
  const [lines, setLines] = useState<LineRow[]>([
    { lineOrder: 1, description: "", quantity: 1, remarks: "" },
  ]);

  // Supplier IDs already picked in another row — excluded from every other row's dropdown
  // so the same supplier can't be invited twice.
  const selectedSupplierIds = new Set(
    supplierRows.filter((r): r is Extract<SupplierRow, { kind: "registered" }> => r.kind === "registered" && r.supplierId !== "")
      .map((r) => r.supplierId),
  );

  async function handleRequisitionChange(value: string) {
    const id = value ? Number(value) : "";
    setRequisitionId(id);
    if (id === "") return;
    setReqLinesLoading(true);
    try {
      const req = await requisitionApi.get(id as number);
      if (req.lines.length) {
        setLines(req.lines.map((l, i) => ({
          lineOrder: l.lineOrder ?? i + 1,
          description: l.description,
          quantity: l.quantity,
          remarks: "",
        })));
      }
    } finally {
      setReqLinesLoading(false);
    }
  }

  function addSupplierRow(kind: SupplierRow["kind"]) {
    setSupplierRows((prev) => [...prev, kind === "registered" ? { kind, supplierId: "" } : { kind, adHocName: "", adHocEmail: "" }]);
  }
  function removeSupplierRow(idx: number) {
    setSupplierRows((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateRegisteredRow(idx: number, supplierId: number | "") {
    setSupplierRows((prev) => prev.map((r, i) => (i === idx && r.kind === "registered" ? { ...r, supplierId } : r)));
  }
  function updateAdhocRow(idx: number, field: "adHocName" | "adHocEmail", value: string) {
    setSupplierRows((prev) => prev.map((r, i) => (i === idx && r.kind === "adhoc" ? { ...r, [field]: value } : r)));
  }

  const { mutate, loading, error } = useMutation(async () => {
    const suppliers: RfqSupplierInput[] = supplierRows.flatMap((r): RfqSupplierInput[] => {
      if (r.kind === "registered") return r.supplierId ? [{ supplierId: r.supplierId }] : [];
      return r.adHocName.trim() && r.adHocEmail.trim() ? [{ adHocName: r.adHocName.trim(), adHocEmail: r.adHocEmail.trim() }] : [];
    });
    if (!suppliers.length) throw new Error("Invite at least one supplier — pick one from the list or add an ad-hoc one.");
    const result = await rfqApi.create({
      requisitionId: requisitionId !== "" ? (requisitionId as number) : null,
      suppliers,
      deadline,
      submissionMethod,
      lines: lines.map((l) => ({ lineOrder: l.lineOrder, description: l.description, quantity: l.quantity, remarks: l.remarks })),
    });
    navigate(`/procurement/rfqs/${result.id}`);
  });

  function addLine() {
    setLines((prev) => [...prev, { lineOrder: prev.length + 1, description: "", quantity: 1, remarks: "" }]);
  }

  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineOrder: i + 1 })));
  }

  function updateLine(idx: number, field: keyof LineRow, value: string | number) {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  const loadingOptions = reqsLoading || suppliersLoading;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/rfqs" className="text-sm text-emerald-700 hover:underline">&larr; Back to RFQs</Link>
      </div>

      <ProcForm formNo="PROC-FRM-02" title="Request for Quotation">
        {loadingOptions && <ProcLoading label="Loading form options..." />}
        {error && <ProcError message={error} />}

        <ProcSection title="RFQ Details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Linked Requisition (optional)</label>
              <select
                value={requisitionId}
                onChange={(e) => handleRequisitionChange(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              >
                <option value="">— none —</option>
                {(requisitions ?? []).map((r) => (
                  <option key={r.id} value={r.id}>{r.serialNo} — {r.purpose}</option>
                ))}
              </select>
              {reqLinesLoading && <p className="mt-1 text-xs text-slate-400">Loading requisition lines…</p>}
              <p className="mt-1 text-xs text-slate-400">Selecting a requisition replaces the Line Items below with its lines.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Deadline</label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                onClick={(e) => e.currentTarget.showPicker?.()}
                className="w-full rounded-xl border px-3 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Submission Method</label>
              <input
                type="text"
                value={submissionMethod}
                onChange={(e) => setSubmissionMethod(e.target.value)}
                placeholder="Email to procurement@..."
                className="w-full rounded-xl border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Invited Suppliers">
          <p className="mb-3 text-xs text-slate-500">
            Pick any number of registered suppliers and/or add ad-hoc external ones. Every one gets emailed automatically when the RFQ is sent.
          </p>
          <div className="space-y-2">
            {supplierRows.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2">
                {row.kind === "registered" ? (
                  <select
                    value={row.supplierId}
                    onChange={(e) => updateRegisteredRow(idx, e.target.value ? Number(e.target.value) : "")}
                    className="flex-1 rounded-lg border px-2 py-1.5 text-sm"
                  >
                    <option value="">— select supplier —</option>
                    {(supplierOptions ?? [])
                      .filter((s) => s.id === row.supplierId || !selectedSupplierIds.has(s.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.name}{s.email ? ` — ${s.email}` : ""}</option>
                      ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      value={row.adHocName}
                      onChange={(e) => updateAdhocRow(idx, "adHocName", e.target.value)}
                      placeholder="Company name"
                      className="flex-1 rounded-lg border px-2 py-1.5 text-sm"
                    />
                    <input
                      type="email"
                      value={row.adHocEmail}
                      onChange={(e) => updateAdhocRow(idx, "adHocEmail", e.target.value)}
                      placeholder="email@example.com"
                      className="flex-1 rounded-lg border px-2 py-1.5 text-sm"
                    />
                    <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Ad-hoc</span>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => removeSupplierRow(idx)}
                  disabled={supplierRows.length === 1}
                  className="shrink-0 text-red-500 hover:text-red-700 disabled:opacity-30 text-xs font-bold"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => addSupplierRow("registered")}
              className="rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              + Add Registered Supplier
            </button>
            <button
              type="button"
              onClick={() => addSupplierRow("adhoc")}
              className="rounded-lg border border-amber-500 px-3 py-1.5 text-sm font-semibold text-amber-700 hover:bg-amber-50"
            >
              + Add Ad-hoc Supplier
            </button>
          </div>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right w-24">Qty</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lines.map((line, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-slate-400">{line.lineOrder}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLine(idx, "description", e.target.value)}
                        placeholder="Item description"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={line.quantity}
                        min={1}
                        onChange={(e) => updateLine(idx, "quantity", Number(e.target.value))}
                        className="w-full rounded-lg border px-2 py-1.5 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.remarks ?? ""}
                        onChange={(e) => updateLine(idx, "remarks", e.target.value)}
                        placeholder="Optional remarks"
                        className="w-full rounded-lg border px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length === 1}
                        className="text-red-500 hover:text-red-700 disabled:opacity-30 text-xs font-bold"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={addLine}
            className="mt-3 rounded-lg border border-emerald-600 px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
          >
            + Add Line
          </button>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/rfqs" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => mutate()}
            disabled={loading}
            className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save RFQ"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
